import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Package, Plus, Trash2, Pencil, History, Building2 } from "lucide-react";

const emptyEntry = {
  buyer_firm_name: "", buyer_gstin: "", product_sold: "", quantity: "", invoice_date: "", city: "",
};

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyEntry);

  const loadEntries = () => api.get(`/products/${productId}/past-performance`).then((r) => setEntries(r.data));
  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => setProduct(r.data)).catch(() => navigate("/products"));
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const openNew = () => { setEdit(null); setForm(emptyEntry); setOpen(true); };
  const openEdit = (e) => {
    setEdit(e);
    setForm({
      buyer_firm_name: e.buyer_firm_name || "", buyer_gstin: e.buyer_gstin || "",
      product_sold: e.product_sold || "", quantity: e.quantity || "",
      invoice_date: e.invoice_date || "", city: e.city || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.buyer_firm_name.trim()) return toast.error("Buyer firm name is required");
    try {
      if (edit) await api.put(`/past-performance/${edit.id}`, form);
      else await api.post(`/products/${productId}/past-performance`, form);
      toast.success(edit ? "Entry updated" : "Past performance entry added");
      setOpen(false);
      loadEntries();
    } catch { toast.error("Could not save entry"); }
  };

  const remove = async (id) => {
    await api.delete(`/past-performance/${id}`);
    toast.success("Entry removed");
    loadEntries();
  };

  const fields = [
    { key: "buyer_firm_name", label: "Buyer Firm Name", placeholder: "M/s ABC Enterprises", required: true },
    { key: "buyer_gstin", label: "GST Number of Buyer Firm", placeholder: "09ABCDE1234F1Z5" },
    { key: "product_sold", label: "Product Sold", placeholder: "ICU Bed (Model X)" },
    { key: "quantity", label: "Quantity", placeholder: "25" },
    { key: "invoice_date", label: "Invoice Date", type: "date" },
    { key: "city", label: "City", placeholder: "Ghaziabad" },
  ];

  if (!product) return <div className="p-12 text-slate-500">Loading…</div>;

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <button onClick={() => navigate("/products")} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-products">
        <ArrowLeft className="h-4 w-4 mr-1" /> Products Library
      </button>

      <div className="flex items-center gap-3 mt-4">
        <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
          <Package className="h-5 w-5 text-slate-900" />
        </div>
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="product-detail-name">{product.name}</h1>
          {product.details && <p className="text-sm text-slate-500">{product.details}</p>}
        </div>
      </div>

      <Tabs defaultValue="past" className="mt-8">
        <TabsList data-testid="product-tabs">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past-performance">
            <History className="h-4 w-4 mr-1.5" /> Past Performance {entries.length ? `(${entries.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <Card className="p-6 rounded-xl border-border">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Specification / Details</div>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{product.details || "No details added."}</p>
          </Card>
        </TabsContent>

        <TabsContent value="past" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="text-sm text-slate-500">Sales history for this product — reuse it as supporting evidence in bids.</p>
            <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-past-performance-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Entry
            </Button>
          </div>

          {entries.length === 0 ? (
            <Card className="p-10 rounded-xl border-border text-center text-sm text-slate-500" data-testid="past-performance-empty">
              <Building2 className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              No past performance recorded yet. Add your first sale above.
            </Card>
          ) : (
            <Card className="rounded-xl border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="past-performance-table">
                  <thead>
                    <tr className="bg-slate-900 text-white text-left">
                      <th className="px-4 py-3 font-semibold">Buyer Firm</th>
                      <th className="px-4 py-3 font-semibold">GST No.</th>
                      <th className="px-4 py-3 font-semibold">Product Sold</th>
                      <th className="px-4 py-3 font-semibold">Qty</th>
                      <th className="px-4 py-3 font-semibold">Invoice Date</th>
                      <th className="px-4 py-3 font-semibold">City</th>
                      <th className="px-4 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={e.id} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`} data-testid={`past-performance-row-${e.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{e.buyer_firm_name}</td>
                        <td className="px-4 py-3 text-slate-600">{e.buyer_gstin || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{e.product_sold || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{e.quantity || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{e.invoice_date || "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{e.city || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`edit-pp-${e.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(e.id)} data-testid={`delete-pp-${e.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{edit ? "Edit Past Performance" : "Add Past Performance"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.key === "buyer_firm_name" ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-semibold text-slate-600">{f.label}{f.required && " *"}</Label>
                <Input
                  data-testid={`pp-${f.key}-input`}
                  type={f.type || "text"}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={submit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="save-pp-btn">Save Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
